import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { AlertEntity, WorkbenchAlert } from '../types';
import {
  composeEntityPrimary,
  composeEntitySecondary,
  entityPrimaryIsRate,
  formatEntityRate,
  formatStayDuration,
} from '../utils/alertTextComposer';

interface AlertEntityRowProps {
  alert: WorkbenchAlert;
  entity: AlertEntity;
}

/**
 * 告警明细行：有下钻数据时可展开，展开后列出扩展维度（如线路下的承跑司机）。
 * 实时告警与周期复盘的上游明细同为 `TaskDetailDTO`，故共用本组件。
 */
const AlertEntityRow: React.FC<AlertEntityRowProps> = ({ alert, entity }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const subEntities = entity.subEntities ?? [];
  const expandable = subEntities.length > 0;
  // 妥投率卡的司机行：主值出妥投率而非件数，停留时长占次要位。
  // 无值出「—」而非空白，空白分不清「没数据」与「渲染挂了」
  const isDriverRateRow = entityPrimaryIsRate(alert) && entity.entityType === 'DRIVER';
  // 「停留时长」标题省掉：一行只放「司机名 + 时长 + 妥投率」，加标题会挤宽整列。
  // 投诉卡的次要位出司机所属 DSP（PRD 口径）：处置动作是约谈 DSP，命中天数在这张卡上帮不上决策。
  // 领取率/妥投率卡的线路行：上游给了本线路的率，出在次要位比留空更有决策价值
  const secondaryText = isDriverRateRow
    ? formatStayDuration(entity.stayMinutes)
    : alert.alertTypeCode === 'COMPLAINT'
      ? entity.dspName ?? ''
      : composeEntitySecondary(alert, entity.secondaryValue)
        || (entity.rate ? formatEntityRate(entity.rate) : '')
        || entity.dspName
        || '';

  return (
    // 明细行套在主值列内（「展开明细」在外层），这样下钻数值的右边缘与本行主值对齐
    <div className="py-2 flex items-start gap-2">
      <div className="flex-1">
        {/* 两列布局：左列名字可自换行到多行，右列（次要文案 + 主值）整体保持在首行右端，
            不出现「名字太长把数字挤到下一行」的错位 */}
        <div className="flex items-start justify-between gap-2 text-sm">
          <span className="font-medium text-gray-800 break-words min-w-0 flex-1">
            {entity.entityName}
          </span>
          <div className="shrink-0 flex items-center gap-2 whitespace-nowrap">
            <span className="text-xs text-gray-500">
              {secondaryText}
            </span>
            <span className="font-semibold text-violet-600">
              {isDriverRateRow
                ? formatEntityRate(entity.rate)
                : composeEntityPrimary(alert, entity.primaryValue)}
            </span>
          </div>
        </div>

        {expandable && expanded && (
          <div className="mt-1.5 ml-3 pl-3 border-l border-violet-200 space-y-1">
            {subEntities.map((sub) => (
              <div
                key={`${sub.entityType}-${sub.entityId}`}
                className="flex items-start justify-between gap-2 text-xs"
              >
                <span className="text-gray-600 break-words min-w-0 flex-1">
                  {sub.entityName}
                </span>
                <div className="shrink-0 flex items-center gap-2 whitespace-nowrap">
                  <span className="text-gray-400">
                    {sub.secondaryValue || sub.dspName}
                  </span>
                  <span className="font-medium text-gray-700">
                    {composeEntityPrimary(alert, sub.primaryValue)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {expandable && (
        <button
          type="button"
          className="text-xs text-violet-600 hover:underline shrink-0 flex items-center gap-0.5 py-1"
          onClick={() => setExpanded((prev) => !prev)}
        >
          {String(t(expanded ? 'workbench.collapseDetail' : 'workbench.expandDetail'))}
          {expanded ? <UpOutlined /> : <DownOutlined />}
        </button>
      )}
    </div>
  );
};

export default AlertEntityRow;
